import {
	SystemConnections,
	SystemConnection,
} from '@Types/systemConnections.types';

import { addNamespace } from '@Utils/actions';
import { createAction } from '@reduxjs/toolkit';
import { TestStatusType } from '@Components/TestCard/TestCard.types';
import { SystemConnectionTestAsyncResponse } from '@Types/systemConnectionTest';

const createActionName = (...p: string[]) => p.join('/');

const ACTION_CONTEXT = 'systemConnections';
const CONNECTION_TEST = 'test';
const POLL = 'poll';
const ASYNC = 'async';

const toResolved = (p: string) => createActionName(p, 'resolved');
const toRejected = (p: string) => createActionName(p, 'rejected');
const toThunk = (
	p: string,
): { action: string; resolved: string; rejected: string } => {
	const actionName = addNamespace(p);
	return {
		action: actionName,
		resolved: toResolved(actionName),
		rejected: toRejected(actionName),
	};
};

export const getSystemConnectionById = createAction<string>(
	addNamespace('systemConnections/getSystemConnectionById'),
);
export const getSystemConnectionByIdResolved = createAction<SystemConnection>(
	addNamespace('systemConnections/getSystemConnectionById/resolved'),
);
export const getSystemConnectionByIdRejected = createAction(
	addNamespace('systemConnections/getSystemConnectionById/rejected'),
);

export const getSystemConnections = createAction(
	addNamespace('systemConnections/getSystemConnections'),
);
export const getSystemConnectionsResolved = createAction<SystemConnections>(
	addNamespace('systemConnections/getSystemConnections/resolved'),
);
export const getSystemConnectionsRejected = createAction(
	addNamespace('systemConnections/getSystemConnections/rejected'),
);

export const createSystemConnection = createAction<{ [args: string]: string }>(
	addNamespace('systemConnections/createSystemConnections'),
);
export const createSystemConnectionResolved = createAction(
	addNamespace('systemConnections/createSystemConnections/resolved'),
);
export const createSystemConnectionRejected = createAction(
	addNamespace('systemConnections/createSystemConnections/rejected'),
);

export const updateSystemConnection = createAction<{
	connectionId: string;
	[args: string]: string;
}>(addNamespace('systemConnections/updateSystemConnection'));
export const updateSystemConnectionResolved = createAction<{
	[args: string]: string;
}>(addNamespace('systemConnections/updateSystemConnection/resolved'));
export const updateSystemConnectionRejected = createAction(
	addNamespace('systemConnections/updateSystemConnection/rejected'),
);

export const deleteSystemConnection = createAction<{
	connectionId: string;
}>(addNamespace('systemConnections/deleteSystemConnection'));
export const deleteSystemConnectionResolved = createAction(
	addNamespace('systemConnections/deleteSystemConnection/resolved'),
);
export const deleteSystemConnectionRejected = createAction(
	addNamespace('systemConnections/deleteSystemConnection/rejected'),
);

/**
 * @deprecated
 */
export const testSystemConnection = createAction<{
	credentials: Record<string, string>;
}>(addNamespace('systemConnections/testSystemConnection'));
/**
 * @deprecated
 */
export const testSystemConnectionResolved = createAction<TestStatusType>(
	addNamespace('systemConnections/testSystemConnection/resolved'),
);
/**
 * @deprecated
 */
export const testSystemConnectionRejected = createAction<TestStatusType>(
	addNamespace('systemConnections/testSystemConnection/rejected'),
);

export const resetSystemConnectionTestStatus = createAction(
	addNamespace('systemConnections/testSystemConnection/reset'),
);

// * system connection test
const SYS_CON_TEST_THUNK = toThunk(
	createActionName(ACTION_CONTEXT, CONNECTION_TEST, ASYNC),
);
const POLL_SYS_CON_TEST_THUNK = toThunk(
	createActionName(ACTION_CONTEXT, CONNECTION_TEST, ASYNC, POLL),
);
const POLL_SYS_CON_TEST_STOP_THUNK = toThunk(
	addNamespace(createActionName(POLL_SYS_CON_TEST_THUNK.action, 'stop')),
);
const POLL_SYS_CON_TEST_T_OUT = addNamespace(
	createActionName(POLL_SYS_CON_TEST_THUNK.action, 'tout'),
);
// ? system connection test async
export const systemConnectionTestAsync = createAction<{
	credentials: Record<string, string>;
	timeoutSec: number;
}>(SYS_CON_TEST_THUNK.action);
export const systemConnectionTestAsyncResolved = createAction<TestStatusType>(
	SYS_CON_TEST_THUNK.resolved,
);
export const systemConnectionTestAsyncRejected = createAction<TestStatusType>(
	SYS_CON_TEST_THUNK.rejected,
);
// ? system connection test poll
export type AsyncPollSysConnTestActionPayload = {
	timeoutSec: number;
	responseSysConTest: SystemConnectionTestAsyncResponse;
};
export const systemConnectionTestAsyncPoll =
	createAction<AsyncPollSysConnTestActionPayload>(
		POLL_SYS_CON_TEST_THUNK.action,
	);
export const systemConnectionTestAsyncPollResolved = createAction(
	POLL_SYS_CON_TEST_THUNK.resolved,
);
export const systemConnectionTestAsyncPollRejected = createAction(
	POLL_SYS_CON_TEST_THUNK.rejected,
);
// ? system connection test poll stop
export const systemConnectionTestAsyncPollStop = createAction(
	POLL_SYS_CON_TEST_STOP_THUNK.action,
);
// ? system connection test poll timeout
export const systemConnectionTestAsyncPollTimeout = createAction(
	POLL_SYS_CON_TEST_T_OUT,
);
